import { FadeIn } from "@xola/ui-kit";
import { sumBy } from "lodash";
import React from "react";
import { Purchase } from "types";
import { almostEquals } from "../../../helpers/mathHelper";
import { useSeller } from "../../../hooks/useAccount";
import { numberFormat } from "../../../utils/numbers";
import { Breakdown } from "../../Common/Breakdown";
import { ProductName } from "../../PurchaseModification/PaymentBreakdown";
import { SplitPayDemographics, SplitPayFieldValue, SplitPaymentInitialValues } from "./CreatePurchaseSplitPayTypes";
import { getDemographicsTotalSum } from "./helpers";

interface SplitPaymentBreakdownProps {
    splitPayDemographics: SplitPayDemographics;
    purchase: Purchase;
}

export const SplitPayBreakdown = ({ splitPayDemographics, purchase }: SplitPaymentBreakdownProps) => {
    const sum = getDemographicsTotalSum(splitPayDemographics, purchase);

    return (
        <FadeIn>
            <Breakdown currency={purchase.currency}>
                <div>
                    <div className="pb-4 pt-2 text-lg font-bold">Split Payment Summary</div>
                    <div className="space-y-4" data-testid="split-payment-summary">
                        {splitPayDemographics.items.length > 1 ? (
                            splitPayDemographics.items.map((item) => {
                                const purchaseItem = purchase.items.find((itemItr) => itemItr.id === item.id);
                                if (purchaseItem && hasQuantity(item)) {
                                    return (
                                        <div key={item.id}>
                                            <ProductName id={purchaseItem.product.id} />
                                            <SplitPaymentDetailsForItem
                                                splitPayDemographics={item.splitPayItemValues}
                                            />
                                        </div>
                                    );
                                }

                                return null;
                            })
                        ) : (
                            <SplitPaymentDetailsForItem
                                splitPayDemographics={splitPayDemographics.items[0].splitPayItemValues}
                            />
                        )}
                    </div>
                    <Breakdown.Separator />
                    <Breakdown.SubtotalItem info="Total Split Payment" value={sum} />
                </div>
            </Breakdown>
        </FadeIn>
    );
};

const SplitPaymentDetailsForItem = ({ splitPayDemographics }: { splitPayDemographics: SplitPaymentInitialValues }) => {
    return (
        <div>
            {splitPayDemographics.demographics.map(
                ({ id, quantity, name, amount }) =>
                    quantity > 0 && (
                        <SplitPayBreakDownItem key={id} id={id} quantity={quantity} amount={amount} name={name} />
                    ),
            )}
            {splitPayDemographics.addons.map(
                ({ id, quantity, name, amount }) =>
                    quantity > 0 && (
                        <SplitPayBreakDownItem key={id} id={id} quantity={quantity} amount={amount} name={name} />
                    ),
            )}
            {!almostEquals(splitPayDemographics.taxes, 0) && (
                <Breakdown.Item value={Math.abs(splitPayDemographics.taxes)} color="primary">
                    Taxes, Fees and other charges
                </Breakdown.Item>
            )}
        </div>
    );
};

const SplitPayBreakDownItem = ({ id, quantity, amount, name }: SplitPayFieldValue) => {
    const seller = useSeller();

    return (
        <Breakdown.Item key={id} value={Math.abs(amount)} color="primary">
            {name}{" "}
            <span className="whitespace-nowrap">
                {[numberFormat(amount / quantity, seller.currency), "×", quantity ?? 1].join(" ")}
            </span>
        </Breakdown.Item>
    );
};

function hasQuantity(item: { id: string; splitPayItemValues: SplitPaymentInitialValues }) {
    return (
        sumBy(item.splitPayItemValues.addons, "quantity") +
            sumBy(item.splitPayItemValues.demographics, "quantity") +
            item.splitPayItemValues.taxes >
        0
    );
}
